import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AuditLogEntry } from '../types';
import { AuditLogService } from '../services/api';
import { Card } from '../components/ui/Card';

const AuditLogContainer = styled.div`
  padding: ${({ theme }) => theme.spacing[60]};
`;

const PageTitle = styled.h1`
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.heading.size.xl};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  margin-bottom: ${({ theme }) => theme.spacing[60]};
`;

const FilterContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[40]};
  margin-bottom: ${({ theme }) => theme.spacing[60]};
  align-items: center;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[20]};
  align-items: center;
`;

const FilterLabel = styled.label`
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  white-space: nowrap;
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing[30]} ${({ theme }) => theme.spacing[40]};
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  border-radius: ${({ theme }) => theme.border.radius.md};
  background-color: ${({ theme }) => theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.body.size.md};
  min-width: 120px;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.primary.default};
  }
`;

const DateInput = styled.input`
  padding: ${({ theme }) => theme.spacing[30]} ${({ theme }) => theme.spacing[40]};
  border: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  border-radius: ${({ theme }) => theme.border.radius.md};
  background-color: ${({ theme }) => theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.default};
  font-size: ${({ theme }) => theme.typography.body.size.md};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.palette.primary.default};
  }
`;

const AuditTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${({ theme }) => theme.spacing[40]};
`;

const TableHeader = styled.th`
  background-color: ${({ theme }) => theme.palette.background.alternate};
  color: ${({ theme }) => theme.palette.text.default};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
  padding: ${({ theme }) => theme.spacing[40]};
  text-align: left;
  border-bottom: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${({ theme }) => theme.palette.background.muted};
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.palette.background.alternate};
  }
`;

const TableCell = styled.td`
  padding: ${({ theme }) => theme.spacing[40]};
  border-bottom: ${({ theme }) => theme.border.width.sm} solid ${({ theme }) => theme.palette.border.default};
  font-size: ${({ theme }) => theme.typography.body.size.sm};
  color: ${({ theme }) => theme.palette.text.default};
`;

const ActionBadge = styled.span<{ action: string }>`
  display: inline-block;
  padding: ${({ theme }) => theme.spacing[10]} ${({ theme }) => theme.spacing[20]};
  border-radius: ${({ theme }) => theme.border.radius.sm};
  font-size: ${({ theme }) => theme.typography.body.size.xs};
  font-weight: ${({ theme }) => theme.typography.weight.medium};
  text-transform: uppercase;
  
  ${({ action, theme }) => {
    switch (action.toLowerCase()) {
      case 'create':
      case 'created':
      case 'create_work_order':
        return `
          background-color: ${theme.palette.background.success};
          color: ${theme.palette.success.default};
        `;
      case 'update':
      case 'updated':
      case 'view_bom':
        return `
          background-color: ${theme.palette.background.emphasis};
          color: ${theme.palette.primary.default};
        `;
      case 'delete':
      case 'deleted':
        return `
          background-color: ${theme.palette.background.critical};
          color: ${theme.palette.danger.default};
        `;
      default:
        return `
          background-color: ${theme.palette.background.alternate};
          color: ${theme.palette.text.support};
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[100]};
  color: ${({ theme }) => theme.palette.text.support};
`;

const LoadingState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing[100]};
  color: ${({ theme }) => theme.palette.text.support};
`;

const AuditLog: React.FC = () => {
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params: any = {
        limit: 100
      };

      if (filter !== 'all') {
        params.entityType = filter;
      }
      
      if (startDate) {
        params.startDate = new Date(startDate).toISOString();
      }
      
      if (endDate) {
        // Set end date to end of day
        const endDateTime = new Date(endDate);
        endDateTime.setHours(23, 59, 59, 999);
        params.endDate = endDateTime.toISOString();
      }

      const logs = await AuditLogService.getAllAuditLogs(params);
      setAuditLogs(logs);
      setFilteredLogs(logs);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, [filter, startDate, endDate]);

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const getEntityTypes = () => {
    const types = ['all', ...new Set(auditLogs.map(log => log.entityType))];
    return types;
  };

  const getUserDisplay = (log: AuditLogEntry) => {
    if (log.userInfo) {
      return log.userInfo.name || log.userInfo.username;
    }
    return log.userId;
  };

  if (loading) {
    return (
      <AuditLogContainer>
        <PageTitle>Audit Log</PageTitle>
        <Card>
          <LoadingState>Loading audit logs...</LoadingState>
        </Card>
      </AuditLogContainer>
    );
  }

  if (error) {
    return (
      <AuditLogContainer>
        <PageTitle>Audit Log</PageTitle>
        <Card>
          <EmptyState>{error}</EmptyState>
        </Card>
      </AuditLogContainer>
    );
  }

  return (
    <AuditLogContainer>
      <PageTitle>Audit Log</PageTitle>
      
      <FilterContainer>
        <FilterGroup>
          <FilterLabel>Filter by Type:</FilterLabel>
          <FilterSelect value={filter} onChange={(e) => setFilter(e.target.value)}>
            {getEntityTypes().map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type}
              </option>
            ))}
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>From:</FilterLabel>
          <DateInput
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>To:</FilterLabel>
          <DateInput
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </FilterGroup>
      </FilterContainer>

      <Card>
        {filteredLogs.length === 0 ? (
          <EmptyState>No audit log entries found.</EmptyState>
        ) : (
          <AuditTable>
            <thead>
              <tr>
                <TableHeader>Timestamp</TableHeader>
                <TableHeader>User</TableHeader>
                <TableHeader>Action</TableHeader>
                <TableHeader>Entity Type</TableHeader>
                <TableHeader>Entity ID</TableHeader>
                <TableHeader>Details</TableHeader>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>{formatTimestamp(log.timestamp)}</TableCell>
                  <TableCell>{getUserDisplay(log)}</TableCell>
                  <TableCell>
                    <ActionBadge action={log.action}>{log.action}</ActionBadge>
                  </TableCell>
                  <TableCell>{log.entityType}</TableCell>
                  <TableCell>{log.entityId}</TableCell>
                  <TableCell>
                    {log.details ? JSON.stringify(log.details, null, 2) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </AuditTable>
        )}
      </Card>
    </AuditLogContainer>
  );
};

export default AuditLog;